import { ThumbnailClientModel } from '@rightship-tech/common-ui/models/psc/psc.models';
import { EntityAttachmentModel } from '@rightship-tech/common-ui/models/attachments.models';
import { IEntityTypeIdentifier } from '@rightship-tech/common-ui/models/basic.models';

export interface GetRSInspectionOutcomeResponse {
    regulatory: number[];
    regulatoryCount: number;
    industryStandard: number[];
    industryStandardCount: number;
    highRiskCount: number;
    mediumRiskCount: number;
    lowRiskCount: number;
    allowShareOutcome: boolean;
    vesselImo: string;
    vesselName: string;
}

export interface RightshipInspectionFleetChartResponse {
    charts: FleetBenchMarkingChartData[];
    max: number;
    maxDwt: number;
    minDwt: number;
    rsInspFleetBenchmarkStartDate: string;
    inspectionBeforeFleetBenchmarkStartDate: boolean;
    vesselType: string;
}

export interface FleetBenchMarkingChartSettings {
    maxValue: number;
    step: number;
    numberOfSteps: number;
    legends: FleetBenchMarkingChartLegend[];
}

export interface FleetBenchMarkingChartLegend {
    name: string;
    cssClass: string
}

export interface FleetBenchMarkingChartData {
    rating: string,
    findings: FleetBenchMarkingFinding[]
}

export interface FleetBenchMarkingFinding {
    identifier: string,
    value: number
}

export interface GetRSInspectionNonConformityResponse {
    sectionNonConformityCounts: NonConformitySectionCountsModel;
    nonConformityRFI: RSInspetionNonConformityRFIModel;
    nonConformities: RightShipInspectionNonConformityModel[];
    nonConformityCloseoutSubtaskType?: number;
}

export interface NonConformitySectionCountsModel {
    [section: string]: { count: number, isHidden: boolean }
}

export interface RSInspetionNonConformityRFIModel {
    subtaskId: number;
    subtaskStatusId?: number;
    subtaskStatus?: string;
    createdDate?: Date;
    statusDate?: Date;
    subtaskName?: string;
}

export interface RightShipInspectionDeficiencyClientModel {
    rightShipInspectionDeficiencyId: number;
    deficiencyRiskAreaId?: number;
    deficiencyCategoryId?: number;
    deficiencyId?: number;
    deficiency: string;
    code: string;
    group: string;
    likelyToOccurInVessel: string;
    likelyToOccurInFleet: string;
    comment: string;
    reason: string;
    createdDate?: Date;
    lastModifiedDate?: Date;
    vetSuperOutcomeLastModifiedDate?: Date;
    vetSuperOutcomeModifiedBy?: number;
    vetSuperOutcomeId?: number;
    vetSuperModifiedOutcome?: boolean;
    manualDefectText?: string;
}

export interface RightShipInspectionStatusClientModel {
    name: string;
    statusId: number;
    subtaskTypes?: number[];
    comment?: string;
    fullComment?: string;
    user: string;
    date: Date;
    previousStatusId?: number;
    inspectorName?: string; 
}

export interface RightShipInspectionReportClientModel {
    index: number;
    rightShipInspectionReportId: number;
    value: string;
    bookmark: string;
    title: string;
}

export interface CargillInspectionDetailModel {
    shipSuitable?: boolean;
    accommodationSeverity?: number;
    accommodationProbability?: number;
    cargoAndBallastSystemsSeverity?: number;
    cargoAndBallastSystemsProbability?: number;
    hullAndDeckItemsSeverity?: number;
    hullAndDeckItemsProbability?: number;
    machinerySpacesSeverity?: number;
    machinerySpacesProbability?: number;
    pollutionManagementSeverity?: number;
    pollutionManagementProbability?: number;
    safetyManagementSeverity?: number;
    safetyManagementProbability?: number;
    navigationSeverity?: number;
    navigationProbability?: number;
    crewManagementSeverity?: number;
    crewManagementProbability?: number;
    certificationAndDocumentationSeverity?: number;
    certificationAndDocumentationProbability?: number;

}

export interface RightShipInspectionInvoiceModel {
    rightShipInspectionInvoiceId?: number;
    rightShipInspectionId?: number;
    billingCustomerId?: number;
    invoiceStatusId?: number;
    contactName?: string;
    companyName?: string;
    address?: string;
    city?: string;
    state?: string;
    countryId?: number;
    country?: string;
    postCode?: string;
    billingInstructions?: string;
    phoneNumber?: string;
    phoneNumberWithoutCode?: string;
    phoneNumberCountryCode?: string;
    email?: string;
    additionalEmailsToNotify?: string;
    invoiceNumber?: string;
    billingCustomer?: string;
    billingCustomerCode?: string;
    supplierInvoiceCurrency?: string;
    supplierInspectionAmount?: number;
    supplierTravelAndExpenseAmount?: number;
    legacySupplierInvoiceTotal?: number;
    salesCurrency?: string;
    salesAmount?: number;
    invoiceStatus?: string;
    inspectionMarkup?: number;
    invoiceReceivedDate?: Date;
    purchaseOrderNo?: string;
    billingAccountId?: number;
    billingAccount?: string;
    cancelledInspectionPenalty?: number;
    lateInspectionPenalty?: number;
    otherPenaltyAmount?: number;
    totalSupplierInvoiceAmount?: number;
    currencyConversionRateId?: number;
    currencyConversionRate?: number;
    supplierTravelAndExpenseAmountUSD?: number;
    totalSupplierInvoiceAmountUSD?: number;
    generatedInvoiceNumber?: string;
}

export interface RightShipInspectionClientModel extends IEntityTypeIdentifier {
    notifyShipManagerOnCloseOut?: boolean;
    allDeficienciesReviewed?: boolean;
    fullInspectionReportReviewed?: boolean;
    vetTaskId?: number;
    vetId?: number;
    overallAssessmentId?: number;
    inspectingCompany?: string;
    inspectingCompanyEmail?: string;
    inspectorId?: number;
    inspector2Id?: number;
    inspector?: string;
    inspector2?: string;
    reasonForInspection?: string;
    reasonForInspectionId?: number;
    inspectingCompanyId?: number;
    validityExpiryDate?: Date;
    emails?: SubtaskCorrespondenceEmailModel[];
    rightShipInspectionId: number;
    id: number;
    vesselId: number;
    vesselDwt?: number;
    inspectionTypeId?: number;
    vesselType?: string;
    additionalEmailsToNotify?: string;
    vesselName?: string;
    vesselFlag?: string;
    inspectionRequestComments?: string;
    requiresRightshipInspectionComments?: string;
    rightShipInspectionStatus?: string;
    vesselImo?: string;
    vehicleIdentificationNumber?: string;
    europeanNumberOfIdentification?: string;
    port?: string;
    portId?: number;
    requestedDateOfInspection?: Date;
    actualDateOfInspection?: Date;
    customerId?: number;
    billingCustomer?: Date;
    country?: string;
    countryId?: number;
    vesselDateOfBuild?: Date;
    estimatedArrivalDate?: Date;
    assignedTo?: string;
    createdBy?: string;
    rating?: string;
    assignedUserId?: number;
    safetyRatingType?: number;
    starRating?: number;
    ratingPlus?: boolean;
    rightShipInspectionStatusId?: number;
    deficiencies?: RightShipInspectionDeficiencyClientModel[];
    statuses?: RightShipInspectionStatusClientModel[];
    reportData: { [index: number]: RightShipInspectionReportClientModel[] };
    cargillInspectionDetails: CargillInspectionDetailModel;
    lengthOfTimeAtBerth?: number;
    lengthOfTimeAtBerthDisplayText?: string;
    reason?: string;
    invoice: RightShipInspectionInvoiceModel;
    documentsCount?: number;
    privateNotesCount?: number;
    correspondenceCount?: number;
    previousValidityExpiryDate?: string;
    rootCauseAnalysisResponseRatingId: number;
    correctiveActionResponseRatingId: number;
    preventiveActionResponseRatingId: number;
    isHybrid?: boolean;
    agentName?: string;
    agentEmail?: string;
    agentOfficeNumber?: string;
    correspondenceEmail?: string;
    technicalManagerEmail?: string;
    inspectorEmail?: string;
    inspector2Email?: string;
    inspectorContactNumber?: string;
    inspector2ContactNumber?: string;
    canForceTabletSync?: boolean;
    useMobileInspectionWorkflow?: boolean;
    cargoOperation?: string;
    formHasBeenSubmitted?: boolean;
    cargoOperationComments?: string;
    rightShipInspectionGradeId?: number;
    rightShipInspectionGrade?: string;
    rightShipInspectionGradeCode?: string;
    rightShipInspectionGradeToolTipText?: string;
    hasSurveyResponse?: boolean;
    tabletSyncStatus?: string;
    validityDateChangedByUserName?: string;
    validityDateChangedOnDate?: Date;
    reviewer?: string;
    hasIncompleteFinding?: boolean;
    hasAssignedInspector?: boolean;
    billingStatusId?: number;
    billingStatus?: string;
    inspectionInvoiceNumberPrefix?: string;
    hasWithdrawnProposal?: boolean;
    hasInspectorUnassigned?: boolean;
    lastRequestedDateOfInspection?: Date;
    hasChangedInspectionDate?: boolean;
    reasonForDurationOfValidityId?: number;
    additionalCommentsForDurationOfValidity?: string;
    createdDate?: Date;
    hasReceivedFinalReport?: boolean;
    pscDate?: Date;
    pscPlace?: string;
    incidentDate?: Date;
    hasFirstReviewDone: boolean,
    firstReviewComments: string,
    firstReviewedBy: string,
    canSendInvoiceEmail: boolean;
    questionNumber?: string;
    hasInspectionOutcomeV2: boolean;
}

export interface SubtaskCorrespondenceEmailModel {
    subtaskCorrespondenceEmailId?: number;
    email: string;
    emailRecipientTypeId?: number;
    canEdit?: boolean;
    isFrom?: boolean;
    isShipManagerEmail?: boolean;
    isFromConfig?: boolean;
    isTechnicalManagerEmail?: boolean;
    isInspectingCompanyEmail?: boolean;
    recipientType?: string;
}

export interface RightShipInspectionNonConformityModel {
    id?: number;
    rightShipInspectionDeficiencyId?: number;
    questionNumber?: string;
    sectionId?: number;
    sectionNumber?: string;
    sectionName?: string;
    sectionSequence: number;
    questionText?: string;
    comment?: string;
    nonConformityClassificationId?: number;
    nonConformityClassificationName?: string;
    nonConformityCategoryId?: number;
    nonConformitySubCategoryId?: number;
    riskLevelClassificationId?: number;
    isBestPractice?: boolean;
    createdDate?: Date;
    lastModifiedDate?: Date;
    vetSuperOutcomeLastModifiedDate?: Date;
    vetSuperOutcomeModifiedBy?: number;
    vetSuperOutcomeId?: number;
    vetSuperModifiedOutcome?: boolean;
    rightshipInspectionFormSubmissionAnswerId?: number;
    nonConformityDescription?: string;
    attachments?: EntityAttachmentModel[];
    riskLevelId?: number;
    riskClassificationId?: number;
    isGradingConfirmed?: boolean;
    isSaveForLater?: boolean;
    peopleClassification?: boolean;
    processClassification?: boolean;
    plantClassification?: boolean;
    findingClassifications: string;
    defaultRiskLevelId?: number;
    defaultRiskClassificationId?: number;
    currentFindingCausations?: FindingCausationNonConformityModel[];
    findingCausations?: any[];
    cosmosExtraction: InspectionCosmosDeficiencyExtractionModel;
    hasCosmosOverriden: boolean;
    element?: HTMLElement;
    findingCausationOverriden?: boolean;
    rootCause?: string;
    preventActions?: string;
    aiExtraction: InspectionCosmosDeficiencyExtractionModel;
    correctiveActions?: string;
    thumbnails?: InspectionThumbnail[];
    aiRunId?: string;
}

export enum RightShipInspectionStatusEnum {
    InspectionRequired = 1,
    NewInspection = 2,
    InProgress = 3,
    Scheduled = 4,
    AwaitingResponse = 5,
    ReviewRequired = 6,
    Acceptable = 7,
    RightShipInspectionReassignment = 8,
    Unacceptable = 9,
    Cancelled = 10,
    InspectorAssigned = 11,
    Booked = 12,
    AlternativeRequired = 13,
    Invalid = 101,
    Incomplete = 102
}

export interface FindingCausationNonConformityModel {
    rightShipInspectionFindingCausationId: number;
    findingCausationL1Id?: number;
    findingCausationL2Id?: number;
    findingCausationL3Id?: number;
    findingCausationL4Id?: number;
    findingCausationL5Id?: number;
    findingCausationL6Id?: number;
    findingCauseOfConcernId?: number;
    rightShipInspectionDeficiencyId?: number;
    isActive: boolean;
    classificationSourceModels: FindingCausationClassificationSourceModel[];
    aiRunId: string;
}

export interface FindingCausationClassificationSourceModel {
    rightshipInspectionId: string;
    rightShipInspectionDeficiencyId: string;
    questionText: string;
    actualFindings: string;
    actualCausation: string;
    actualCauseofConcern: string;
    inspectionDate: string;
}

export interface InspectionCosmosDeficiencyExtractionModel {
    thumbnails: InspectionThumbnail[];
    aiRunId: string;
    rootCause: string;
    preventativeAction: string;
    correctiveAction: string;
    ownerResponse: string;
    isCloseoutOverriden?: boolean;
}

export interface InspectionThumbnail {
    id: string;
    fileName: string;
    page: number;
    src: string;
}

export enum FindingRiskLevelEnum
{
    High = 1,
    Medium = 2,
    Low = 3
}

export interface InsightIndicatorModel {
    id: number;
    count: number;
    name: string;
    insights?: InsightModel[];
}

export interface InsightModel {
    id: number;
    count: number;
    title: string;
    description?: string;
    questions?: InsightQuestion[];
}

export interface InsightQuestion {
    deficiencyId: number;
    questionNumber: string;
    questionName: string;
    attachments?: EntityAttachmentModel[];
}

export interface GetInsightResponse {
    insightIndicators: InsightIndicatorModel[];
}

export enum FindingRiskClassificationEnum
{
    Regulatory = 1,
    IndustryStandard = 2
}

export interface GetRightShipInspectionDocumentLinksResponse {
    documentLinks: RightShipInspectionDocumentLinkItemModel[]
}

export interface RightShipInspectionDocumentLinkItemModel {
    sasUrl: string;
    aiRunId?: string;
    thumbnails: ThumbnailClientModel[]
}

export interface RightShipInspectionFindingsImagesResponse {
    images: RightShipInspectionFindingImageModel[];
}

export interface RightShipInspectionFindingImageModel {
    path: string;
    name: string;
    entityAttachmentId: number;
    fullUrl?: string;
    questionNumber?: string;
    findingDescription?: string;
}

export interface GetUltrasonicReportResponse {
    ultrasonicReports: UltrasonicReport[];
}

export interface UltrasonicReport {
    title: string;
    description: string;
    path: string;
    attachmentEntityId: number;
}
